import React from 'react';
import { type MouseEventHandler } from 'react';
import { Badge, Grid, IconButton, Tooltip } from '@mui/material';

import { AuthorizerContext } from 'app/providers/acl/AclProvider';

import { type FiltersType } from '../../../hooks/useFilters';
import { type QueryType } from '../../../hooks/useQuery';
import useSettings from '../../../hooks/useSettings';
import Icon from '../../../Icon';
import ExportButton from '../../../reporting/ExportButton';
import FilterButtons from '../../../filter/FilterButtons';

interface PaginationAdditionalButtonProps {
	title: string;
	icon: string;
	onClick: MouseEventHandler<HTMLButtonElement>;
	showBadge?: boolean;
}

interface PaginationButtonsProps {
	module: string;
	filterProps?: FiltersType & QueryType;
	visibleFilters: boolean;
	additionalButtons?: PaginationAdditionalButtonProps[];
	showFilters?: boolean;
	showExportButton?: boolean;
}

function PaginationAdditionalButton(props: PaginationAdditionalButtonProps) {
	const { t } = useSettings();

	return (
		<Tooltip title={t(props.title)} arrow>
			<IconButton size="small" onClick={props.onClick}>
				<Badge color="secondary" variant="dot" invisible={!props.showBadge}>
					<Icon icon={props.icon} />
				</Badge>
			</IconButton>
		</Tooltip>
	);
}

export default function PaginationButtons({
	module,
	filterProps,
	visibleFilters,
	additionalButtons,
	showFilters,
	showExportButton
}: PaginationButtonsProps) {
	const authorizer = React.useContext(AuthorizerContext);
	const canExport = authorizer.can('export', module);

	return (
		<Grid container spacing={1}>
			{showFilters && visibleFilters && (
				<Grid>
					<FilterButtons module={module} filterProps={filterProps} />
				</Grid>
			)}
			{showExportButton && canExport && (
				<Grid>
					<ExportButton module={module} filters={filterProps?.filters} query={filterProps?.query} />
				</Grid>
			)}
			{additionalButtons?.map((button, index) => (
				<Grid key={index}>
					<PaginationAdditionalButton {...button} />
				</Grid>
			))}
		</Grid>
	);
}
